<script setup lang="ts">
import type { ContactUsT } from '~/types/contact-us'

useSeoMeta({
  title: 'Aplikasi Kirim Paket COD Terbaik #1 Indonesia | KiriminAja',
  description:
    'Kirim paket ke seluruh Indonesia dengan pengiriman reguler KiriminAja! Ekspedisi terlengkap, bisa COD, &amp; tanpa minimum jumlah kiriman. Kirim sekarang!'
})

const { $toast } = useNuxtApp()

const isLoading = ref(false)
const formValues = ref<ContactUsT>({})
const route = useRoute()

const formErrors = ref<{
  [key: string]: string
}>({})

const { data: dataResources } = await useAsyncData('contactUsResources', () =>
  $fetch('/intl-api/contact-us/resources')
)

const handleSubmit = async () => {
  isLoading.value = true
  formErrors.value = {}

  await $fetch('/intl-api/contact-us', {
    method: 'POST',
    body: { ...formValues.value },
    onResponseError: (error) => {
      isLoading.value = false
      if (error.response.status === 422) {
        const errorData = error.response._data as {
          data?: {
            message?: string
            path?: string[]
          }[]
        }

        errorData.data?.forEach((err) => {
          if (!err.message || formErrors.value[err.path?.[0] || '0']) return
          formErrors.value[err.path?.[0] || '0'] = err.message
        })
      } else {
        $toast.error(
          'Terjadi kesalahan saat mengirim data. Silakan coba lagi nanti.'
        )
      }
    }
  })

  isLoading.value = false
  formValues.value = {}
  $toast.success('Permintaan Anda berhasil kami terima.')
}

onMounted(() => {
  if (route.query.topic) {
    const topicExists = dataResources.value?.topics?.some(
      (topic) => topic.key === route.query.topic?.toString()
    )
    formValues.value.topics = topicExists
      ? [route.query.topic?.toString()]
      : undefined
  }

  if (route.query.message) {
    formValues.value.message = route.query.message?.toString()
  }
})
</script>

<template>
  <div class="flex flex-col">
    <HeroBase
      class="py-10 lg:py-16"
      title="Kontak Kami"
      description="Ajukan pertemuan online/offline dengan kami dan kami akan segera menghubungi untuk pemenuhan kebutuhan bisnis Anda."
      hero-image="/images/kontak-kami-banner.webp"
      image-class="hidden lg:block"
    >
      <template #action>
        <form class="flex flex-col gap-3 py-3" @submit.prevent="handleSubmit">
          <div class="flex flex-col gap-3">
            <div class="grid sm:grid-cols-2 gap-3">
              <UiInputControl label="Nama" for="name" required>
                <UiInputField
                  id="name"
                  v-model="formValues.name"
                  placeholder="Masukkan Nama"
                  required
                  :invalid="!!formErrors['name']"
                />
                <UiLabelErrorLabel
                  v-if="formErrors['name']"
                  :text="formErrors['name']"
                />
              </UiInputControl>
              <UiInputControl
                label="Nama Brand/Perusahaan"
                for="store_name"
                required
              >
                <UiInputField
                  id="store_name"
                  v-model="formValues.store_name"
                  placeholder="Masukkan Nama Brand/Perusahaan"
                  required
                  :invalid="!!formErrors['store_name']"
                />
                <UiLabelErrorLabel
                  v-if="formErrors['store_name']"
                  :text="formErrors['store_name']"
                />
              </UiInputControl>
            </div>
            <div class="grid sm:grid-cols-2 gap-3">
              <UiInputControl label="Email" for="email" required>
                <UiInputField
                  id="email"
                  v-model="formValues.email"
                  placeholder="Masukkan Email"
                  required
                  :invalid="!!formErrors['email']"
                />
                <UiLabelErrorLabel
                  v-if="formErrors['email']"
                  :text="formErrors['email']"
                />
              </UiInputControl>
              <UiInputControl label="Nomor Telepon" for="phone" required>
                <UiInputFieldPhone
                  id="phone"
                  v-model="formValues.phone"
                  placeholder="8XXXXXXXXX"
                  :max-length="15"
                  required
                  :invalid="!!formErrors['phone']"
                />
                <UiLabelErrorLabel
                  v-if="formErrors['phone']"
                  :text="formErrors['phone']"
                />
              </UiInputControl>
              <UiInputControl label="Jabatan" for="position" required>
                <UiInputField
                  id="position"
                  v-model="formValues.position"
                  placeholder="Cth: Pemilik, Staff"
                  required
                  :invalid="!!formErrors['position']"
                />
                <UiLabelErrorLabel
                  v-if="formErrors['position']"
                  :text="formErrors['position']"
                />
              </UiInputControl>
              <UiInputControl
                label="Estimasi Pengiriman Paket (Per Bulan)"
                for="package_estimation"
                required
              >
                <UiSelectBase
                  id="package_estimation"
                  v-model="formValues.package_estimation"
                  :options="dataResources?.package_estimations || []"
                  value-key="key"
                  label-key="label"
                  :invalid="!!formErrors['package_estimation']"
                />
                <UiLabelErrorLabel
                  v-if="formErrors['package_estimation']"
                  :text="formErrors['package_estimation']"
                />
              </UiInputControl>
            </div>
            <UiInputControl
              label="Pilih Topik (Boleh pilih lebih dari 1)"
              for="topics"
              required
            >
              <UiToggleGroup
                id="topics"
                v-model="formValues.topics"
                :options="dataResources?.topics || []"
                value-key="key"
                label-key="label"
                multiple
              />
              <UiLabelErrorLabel
                v-if="formErrors['topics']"
                :text="formErrors['topics']"
              />
            </UiInputControl>
            <UiInputControl label="Tulis Pesan" for="message" required>
              <UiInputTextArea
                id="message"
                v-model="formValues.message"
                placeholder="Ketik Disini"
                required
                :invalid="!!formErrors['message']"
              />
              <UiLabelErrorLabel
                v-if="formErrors['message']"
                :text="formErrors['message']"
              />
            </UiInputControl>
          </div>
          <div class="flex">
            <UiButton
              class="flex-1 max-w-44"
              label="Kirim"
              type="submit"
              :loading="isLoading"
            />
          </div>
        </form>
      </template>
    </HeroBase>
    <CtaSection />
  </div>
</template>
